import React from 'react';

import { Link } from 'react-router-dom';

import {
  GolfTabId,
  golfTabRoute,
} from '../../constants/GolferProfileConstants';
import { useMyStats } from '../../hooks/useMemberCoursesPlayedApi';

// The member's numbers at the top of their dashboard: what they have done, from the data the app
// holds (GET /member/me/stats, see MemberStatsDTO).
//
// "States" are whatever a course's country calls them: a South African province counts the same
// as a US state. "This month" only counts courses added in the new app; courses brought over from
// the legacy system have no real date, which is also why Course Last Added can say "Not known".
//
// Stats the client listed that the app has no data for (handicap, buddies, rounds, people played
// with, investor) are not shown; see docs/FEATURE-BACKLOG.md.
//
// Every tile opens the matching list on the profile's golf page (/member/profile/golf), the same
// place the profile's counters go. Course Last Added opens the courses list.

interface StatTileProps {
  label: string;
  value: React.ReactNode;
  // A second, smaller line, e.g. "+2 this month". Omitted when there is nothing to add.
  detail?: string | null;
  tab: GolfTabId;
}

const StatTile: React.FC<StatTileProps> = ({ label, value, detail, tab }) => (
  <Link
    to={golfTabRoute(tab)}
    className="block rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 transition-colors hover:border-primary hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:border-white/10 dark:bg-white/5 dark:hover:border-primary"
  >
    <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted dark:text-white/60">
      {label}
    </p>
    <p className="mt-1 font-display text-2xl font-semibold tabular-nums text-ink dark:text-white">
      {value}
    </p>
    {detail != null && (
      <p className="mt-0.5 text-xs text-ink-muted dark:text-white/50">
        {detail}
      </p>
    )}
  </Link>
);

const thisMonth = (count: number | undefined): string | null =>
  count != null && count > 0 ? `+${count} this month` : null;

const formatDate = (value: Date | string | undefined): string => {
  if (value == null) {
    return 'Not known';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Not known'
    : date.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
};

const MemberStats: React.FC = () => {
  const { data: stats, isLoading, isError } = useMyStats();

  if (isError) {
    return (
      <p className="mb-4 text-sm text-ink-muted dark:text-white/60">
        Your stats could not be loaded just now.
      </p>
    );
  }

  // Dashes while loading, so the tiles hold their place and nothing jumps when the numbers land.
  const show = (value: number | undefined): React.ReactNode =>
    isLoading || value == null ? '-' : value.toLocaleString();

  return (
    <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
      <StatTile
        label="Courses Played"
        tab="courses"
        value={show(stats?.coursesPlayed)}
        detail={thisMonth(stats?.coursesPlayedThisMonth)}
      />
      <StatTile
        label="Countries Played"
        tab="countries"
        value={show(stats?.countriesPlayed)}
        detail={thisMonth(stats?.countriesPlayedThisMonth)}
      />
      <StatTile
        label="States Played"
        tab="states"
        value={show(stats?.statesPlayed)}
        detail={thisMonth(stats?.statesPlayedThisMonth)}
      />
      <StatTile
        label="Course Last Added"
        tab="courses"
        value={
          <span className="text-lg">
            {isLoading ? '-' : formatDate(stats?.courseLastAddedUtc)}
          </span>
        }
      />
      <StatTile
        label="Home Courses"
        tab="home"
        value={show(stats?.homeCourses)}
      />
      <StatTile
        label="Bucket List"
        tab="bucket"
        value={show(stats?.bucketList)}
      />
    </div>
  );
};

export default MemberStats;
